import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Form, 
  Button, 
  Card, 
  Alert, 
  Spinner, 
  Badge, 
  Modal, 
  ListGroup 
} from 'react-bootstrap';
import Select from 'react-select';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  getFieldMappings, 
  saveFieldMappings, 
  testFieldMappings,
  getSalesforceObjectFields
} from '../api/fieldMappings';
import FieldMappingsSideNav from '../components/FieldMappingsSideNav';
import '../styles/FieldMappings.css';
import { FaCheck, FaSync, FaTimes } from 'react-icons/fa';

/**
 * Field Mappings page
 * Allows admin users to map application fields to Salesforce fields
 */
const FieldMappings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Hardcoded opportunity fields (form fields from the application)
  const hardcodedOpportunityFields = [
    // Business
    { name: 'BusinessLegalName', label: 'Business Legal Name', type: 'string', category: 'Business' },
    { name: 'DBA', label: 'DBA', type: 'string', category: 'Business' },
    { name: 'Industry', label: 'Industry', type: 'string', category: 'Business' },
    { name: 'EntityType', label: 'Entity Type', type: 'string', category: 'Business' },
    { name: 'FederalTaxID', label: 'Federal Tax ID', type: 'string', category: 'Business' },
    { name: 'AnnualRevenue', label: 'Annual Revenue', type: 'string', category: 'Business' },
    { name: 'AverageMonthlySales', label: 'Average Monthly Sales', type: 'string', category: 'Business' },
    { name: 'AverageNSFs', label: 'Average NSFs', type: 'number', category: 'Business' },
    { name: 'AverageNumberOfDeposits', label: 'Average Number of Deposits', type: 'number', category: 'Business' },
    { name: 'AverageNumberOfNegDays', label: 'Average Number of Neg Days', type: 'number', category: 'Business' },
    { name: 'RequestedAmount', label: 'Requested Amount', type: 'string', category: 'Business' },
    { name: 'AverageBankBalance', label: 'Average Bank Balance', type: 'string', category: 'Business' },
    { name: 'BusinessStartDate', label: 'Business Start Date', type: 'date', category: 'Business' },
    { name: 'SICCode', label: 'SIC Code', type: 'string', category: 'Business' },
    { name: 'NumberOfPositions', label: 'Number of Positions', type: 'number', category: 'Business' },
    { name: 'PayoffAmountNeeded', label: 'Payoff Amount Needed', type: 'number', category: 'Business' },
    
    // Business Address
    { name: 'BusinessStreet', label: 'Street', type: 'string', category: 'Business Address' },
    { name: 'BusinessCity', label: 'City', type: 'string', category: 'Business Address' },
    { name: 'BusinessState', label: 'State', type: 'string', category: 'Business Address' },
    { name: 'BusinessZipCode', label: 'Zip/Postal Code', type: 'string', category: 'Business Address' },
    { name: 'BusinessCountry', label: 'Country', type: 'string', category: 'Business Address' },
    
    // Primary Contact
    { name: 'ContactFirstName', label: 'First Name', type: 'string', category: 'Primary Contact' },
    { name: 'ContactLastName', label: 'Last Name', type: 'string', category: 'Primary Contact' },
    { name: 'ContactTitle', label: 'Title', type: 'string', category: 'Primary Contact' },
    { name: 'ContactSSN', label: 'SSN', type: 'string', category: 'Primary Contact' },
    { name: 'ContactDateOfBirth', label: 'Date of Birth', type: 'date', category: 'Primary Contact' },
    { name: 'ContactEmail', label: 'Email', type: 'string', category: 'Primary Contact' },
    { name: 'ContactCellPhone', label: 'Cell Phone', type: 'string', category: 'Primary Contact' },
    { name: 'ContactOwnershipPercentage', label: 'Ownership Percentage', type: 'string', category: 'Primary Contact' },
    { name: 'ContactCreditScore', label: 'Credit Score', type: 'string', category: 'Primary Contact' },
    { name: 'CumulativeOwnershipPercentage', label: 'Cumulative Ownership Percentage', type: 'number', category: 'Primary Contact' },
    
    // Contact Address
    { name: 'ContactStreet', label: 'Street', type: 'string', category: 'Contact Address' },
    { name: 'ContactCity', label: 'City', type: 'string', category: 'Contact Address' },
    { name: 'ContactState', label: 'State', type: 'string', category: 'Contact Address' },
    { name: 'ContactZipCode', label: 'Zip/Postal Code', type: 'string', category: 'Contact Address' },
    { name: 'ContactCountry', label: 'Country', type: 'string', category: 'Contact Address' },
    
    // General
    { name: 'Notes', label: 'Notes', type: 'string', category: 'General' },
    { name: 'ReferralSource', label: 'Referral Source', type: 'string', category: 'General' },
    
    // Bank Statement Data
    { name: 'Month1Deposits', label: 'Month 1 Deposits', type: 'currency', category: 'Bank Statement Data' },
    { name: 'Month2Deposits', label: 'Month 2 Deposits', type: 'currency', category: 'Bank Statement Data' },
    { name: 'Month3Deposits', label: 'Month 3 Deposits', type: 'currency', category: 'Bank Statement Data' },
    { name: 'Month4Deposits', label: 'Month 4 Deposits', type: 'currency', category: 'Bank Statement Data' },
    { name: 'Month1NSFs', label: 'Month 1 NSFs', type: 'number', category: 'Bank Statement Data' },
    { name: 'Month2NSFs', label: 'Month 2 NSFs', type: 'number', category: 'Bank Statement Data' },
    { name: 'Month3NSFs', label: 'Month 3 NSFs', type: 'number', category: 'Bank Statement Data' },
    { name: 'Month4NSFs', label: 'Month 4 NSFs', type: 'number', category: 'Bank Statement Data' },
    { name: 'Month1NegativeDays', label: 'Month 1 Negative Days', type: 'number', category: 'Bank Statement Data' },
    { name: 'Month2NegativeDays', label: 'Month 2 Negative Days', type: 'number', category: 'Bank Statement Data' },
    { name: 'Month3NegativeDays', label: 'Month 3 Negative Days', type: 'number', category: 'Bank Statement Data' },
    { name: 'Month4NegativeDays', label: 'Month 4 Negative Days', type: 'number', category: 'Bank Statement Data' },
    { name: 'Month1DepositCount', label: 'Month 1 Deposit Count', type: 'number', category: 'Bank Statement Data' },
    { name: 'Month2DepositCount', label: 'Month 2 Deposit Count', type: 'number', category: 'Bank Statement Data' },
    { name: 'Month3DepositCount', label: 'Month 3 Deposit Count', type: 'number', category: 'Bank Statement Data' },
    { name: 'Month4DepositCount', label: 'Month 4 Deposit Count', type: 'number', category: 'Bank Statement Data' }
  ];
  
  const [opportunityFields, setOpportunityFields] = useState([]);
  const [salesforceFields, setSalesforceFields] = useState({
    Opportunity: [],
    Account: [],
    Contact: [],
    'Statement__c': []
  });
  const [mappings, setMappings] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showTestResults, setShowTestResults] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [activeSection, setActiveSection] = useState(0);
  const [selectedObject, setSelectedObject] = useState('Opportunity');
  
  // Available Salesforce objects
  const salesforceObjects = [
    { value: 'Opportunity', label: 'Opportunity' },
    { value: 'Account', label: 'Account' },
    { value: 'Contact', label: 'Contact' },
    { value: 'Statement__c', label: 'Statement (Related List)' }
  ];
  
  // Check if user is admin or developer
  useEffect(() => {
    if (user && user.role !== 'admin' && user.role !== 'super_admin' && user.role !== 'developer') {
      navigate('/');
    }
  }, [user, navigate]);
  
  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        console.log('Starting to load field mapping data...');
        setLoading(true);
        setError(null);
        
        // Set opportunity fields directly from hardcoded data
        setOpportunityFields(hardcodedOpportunityFields);
        console.log('Set opportunity fields from hardcoded data:');
        
        // Extract categories from opportunity fields
        const uniqueCategories = [
          ...new Set(hardcodedOpportunityFields.map(field => field.category || 'General'))
        ];
        setCategories(uniqueCategories);
        console.log('Set categories:', uniqueCategories);
        
        // Fetch existing primary field mappings
        console.log('Fetching existing field mappings...');
        const existingMappings = await getFieldMappings();
        console.log('Received existing mappings.');
        const updatedMappings = existingMappings.map(mapping => ({
          ...mapping,
          salesforceObject: mapping.salesforceObject || 'Opportunity'
        }));
        setMappings(updatedMappings);
        console.log('Updated primary mappings with salesforceObject.');
        
        // Preload fields for ALL Salesforce objects at once
        console.log('Preloading fields for all Salesforce objects...');
        
        const fieldPromises = salesforceObjects.map(async (obj) => {
          console.log(`Loading fields for ${obj.value}...`);
          try {
            const fields = await getSalesforceObjectFields(obj.value);
            console.log(`Successfully loaded ${fields.length} fields for ${obj.value}.`);
            return { objectName: obj.value, fields };
          } catch (err) {
            console.error(`Error loading fields for ${obj.value}:`, err);
            return { objectName: obj.value, fields: [] }; // Return empty on error
          }
        });
        
        // Wait for all field loading to complete
        const results = await Promise.all(fieldPromises);
        
        // Update state with all fields at once
        const allFields = results.reduce((acc, result) => {
          acc[result.objectName] = result.fields;
          return acc;
        }, {});
        
        console.log('All Salesforce fields summary: Checking loaded fields...');
        Object.keys(allFields).forEach(objectName => {
          if (!allFields[objectName] || allFields[objectName].length === 0) {
            console.warn(`- WARNING: ${objectName}: No fields loaded or empty array received.`);
          } else {
            console.log(`- ${objectName}: ${allFields[objectName].length} fields loaded successfully.`);
          }
        });
        
        setSalesforceFields(allFields);
        console.log('All Salesforce fields preloaded successfully.');
        
        // Log mapping analysis
        logMappingAnalysis(hardcodedOpportunityFields, updatedMappings, allFields);
        
        console.log('Field mapping data loading complete.');
        setLoading(false);
      } catch (err) {
        console.error('Critical error during data loading:', err);
        setError(`Error loading data: ${err.message || 'Please try again.'}`);
        setLoading(false);
      }
    };
    
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Keep dependency array empty for initial load
  
  // Function to log detailed mapping analysis
  const logMappingAnalysis = (opportunityFields, mappings, salesforceFields) => {
    console.group('DETAILED FIELD MAPPING ANALYSIS');
    console.log('----------');
    
    // Group opportunity fields by category for better readability
    const fieldsByCategory = {};
    opportunityFields.forEach(field => {
      if (!fieldsByCategory[field.category]) {
        fieldsByCategory[field.category] = [];
      }
      fieldsByCategory[field.category].push(field);
    });
    
    // Analyze each category
    Object.entries(fieldsByCategory).forEach(([category, fields]) => {
      console.group(`Category: ${category}`);
      
      fields.forEach(field => {
        const mapping = mappings.find(m => m.opportunityField === field.name);
        
        if (!mapping || !mapping.salesforceField) {
          console.log(`❌ ${field.name} (${field.label}) - NOT MAPPED`);
        } else {
          const sfObject = mapping.salesforceObject;
          const sfField = mapping.salesforceField;
          const sfFieldExists = salesforceFields[sfObject]?.some(f => f.name === sfField);
          
          if (sfFieldExists) {
            const sfFieldDetails = salesforceFields[sfObject].find(f => f.name === sfField);
            console.log(`✅ ${field.name} (${field.label}) → ${sfObject}.${sfField} (${sfFieldDetails.label || 'No Label'})`);
          } else {
            console.log(`⚠️ ${field.name} (${field.label}) → ${sfObject}.${sfField} - WARNING: Target field might not exist!`);
          }
        }
      });
      
      console.groupEnd();
    });
    
    // Count mapping statistics
    const totalFields = opportunityFields.length;
    const mappedFields = mappings.filter(m => m.salesforceField).length;
    const mappedPercentage = Math.round((mappedFields / totalFields) * 100);
    
    console.log('----------');
    console.log(`SUMMARY: ${mappedFields} of ${totalFields} fields mapped (${mappedPercentage}%)`);
    console.groupEnd();
  };
  
  // Calculate mapping stats for sidebar
  const calculateMappingStats = () => {
    const stats = {};
    
    categories.forEach(category => {
      const categoryFields = opportunityFields.filter(field => field.category === category);
      const mappedFieldsCount = categoryFields.filter(field => {
        const mapping = mappings.find(m => m.opportunityField === field.name);
        return mapping && mapping.salesforceField;
      }).length;
      
      stats[category] = {
        mapped: mappedFieldsCount,
        total: categoryFields.length
      };
    });
    
    console.log('Mapping stats:', stats);
    return stats;
  };
  
  // Handle object selection change for a specific mapping
  const handleMappingObjectChange = async (opportunityField, selectedOption) => {
    if (!selectedOption) return;
    
    const objectName = selectedOption.value;
    console.log('handleMappingObjectChange called with:', opportunityField, objectName);
    
    // Update the specific mapping without making API calls
    const newMappings = [...mappings];
    const mappingIndex = newMappings.findIndex(m => m.opportunityField === opportunityField);
    
    if (mappingIndex >= 0) {
      // Update existing mapping
      newMappings[mappingIndex] = {
        ...newMappings[mappingIndex],
        salesforceObject: objectName,
        salesforceField: '' // Reset the field when object changes
      };
    } else {
      // Add new mapping
      newMappings.push({
        opportunityField,
        salesforceObject: objectName,
        salesforceField: ''
      });
    }
    
    console.log('Setting mappings to:', newMappings);
    setMappings(newMappings);
  };
  
  // Handle default object selection change
  const handleObjectChange = (value) => {
    console.log('Changing default object to:', value);
    setSelectedObject(value);
  };
  
  // Handle field selection change
  const handleFieldChange = (opportunityField, salesforceField) => {
    console.log('handleFieldChange called with:', opportunityField, salesforceField);
    
    // Create a new array of mappings
    const newMappings = [...mappings];
    const mappingIndex = newMappings.findIndex(m => m.opportunityField === opportunityField);
    
    if (mappingIndex >= 0) {
      // Update existing mapping
      newMappings[mappingIndex] = {
        ...newMappings[mappingIndex],
        salesforceField
      };
    } else {
      // Add new mapping
      newMappings.push({
        opportunityField,
        salesforceObject: selectedObject,
        salesforceField
      });
    }
    
    console.log('Setting mappings to:', newMappings);
    setMappings(newMappings);
  };
  
  // Custom styles for Select component
  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '32px',
      height: '32px',
      boxShadow: 'none',
      borderColor: '#ced4da',
      fontSize: '0.85rem',
      '&:hover': {
        borderColor: '#80bdff'
      }
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      fontSize: '0.85rem'
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999
    }),
    container: (provided) => ({
      ...provided,
      width: '100%'
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 8px',
      height: '30px'
    }),
    input: (provided) => ({
      ...provided,
      margin: '0',
      padding: '0'
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      margin: '3px 0'
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: '3px 8px'
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: '3px 8px'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#2e3e9f' : state.isFocused ? '#e0e4ff' : null,
      color: state.isSelected ? 'white' : 'inherit',
      cursor: 'pointer',
      fontSize: '0.85rem',
      padding: '5px 12px',
      ':active': {
        backgroundColor: state.isSelected ? '#2e3e9f' : '#c5cdff'
      },
      ':hover': {
        backgroundColor: state.isSelected ? '#2e3e9f' : '#e0e4ff'
      },
      fontWeight: state.isSelected ? '500' : 'normal',
      transition: 'background-color 0.1s ease',
      borderLeft: state.isSelected ? '3px solid #2e3e9f' : state.isFocused ? '3px solid #9aa6ff' : '3px solid transparent',
      paddingLeft: state.isSelected || state.isFocused ? '9px' : '12px' // Adjust padding to compensate for border
    })
  };
  
  // Save mappings
  const handleSave = async () => {
    try {
      if (saving) return;
      setSaving(true);
      setError(null);
      setSuccess(null);
  
      // Filter out invalid primary mappings
      const validPrimaryMappings = mappings.filter(mapping => 
        mapping.opportunityField && mapping.salesforceField && mapping.salesforceObject
      );
  
      console.log('Saving Primary Mappings:', validPrimaryMappings);
  
      // Save field mappings
      await saveFieldMappings(validPrimaryMappings);
  
      setSuccess('Field mappings saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
      setSaving(false);
    } catch (err) {
      console.error('Error saving data:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Error saving data. Please try again.';
      setError(errorMessage);
      setSaving(false);
    }
  };
  
  // Test mappings
  const handleTest = async () => {
    try {
      setTesting(true);
      setError(null);
      setSuccess(null);
      
      // Filter out mappings that don't have both required fields
      const validMappings = mappings.filter(mapping => 
        mapping.opportunityField && mapping.salesforceField
      );
      
      if (validMappings.length === 0) {
        setError('No valid mappings to test. Please map at least one field to Salesforce.');
        setTesting(false);
        return;
      }
      
      const result = await testFieldMappings(validMappings);
      setTestResults(result);
      setShowTestResults(true);
      
      setTesting(false);
    } catch (err) {
      console.error('Error testing field mappings:', err);
      setError('Error testing field mappings. Please try again.');
      setTesting(false);
    }
  };
  
  // Filter fields by search term and active category
  const getFilteredFields = () => {
    const activeCategory = categories[activeSection];
    
    const filteredFields = opportunityFields.filter(field => {
      const matchesCategory = field.category === activeCategory;
      const matchesSearch = !searchTerm || 
        field.label.toLowerCase().includes(searchTerm.toLowerCase()) || 
        field.name.toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesCategory && matchesSearch;
    });
    
    console.log(`Filtered fields for category "${activeCategory}":`, filteredFields.map(f => f.name));
    return filteredFields;
  };
  
  // Helper function to get mapping for a specific field
  const getMappingForField = (opportunityField) => {
    // Find the mapping for this opportunity field
    const mapping = mappings.find(m => m.opportunityField === opportunityField);
    
    // Return the mapping or a default empty mapping
    const result = mapping || { 
      opportunityField,
      salesforceObject: selectedObject,
      salesforceField: ''
    };
    
    // Log when a field has no mapping
    if (!mapping || !mapping.salesforceField) {
      console.log(`No mapping found for field: ${opportunityField}`);
    }
    
    return result;
  };
  
  // Close test results modal
  const handleCloseTestResults = () => {
    setShowTestResults(false);
  };
  
  // Add debug button in development mode
  useEffect(() => {
    // Only add in non-production environment
    if (process.env.NODE_ENV !== 'production') {
      // Create button element
      const debugButton = document.createElement('button');
      debugButton.innerHTML = 'Debug Field Mappings';
      debugButton.style.position = 'fixed';
      debugButton.style.bottom = '10px';
      debugButton.style.right = '10px';
      debugButton.style.zIndex = '9999';
      debugButton.style.padding = '8px 12px';
      debugButton.style.backgroundColor = '#f8f9fa';
      debugButton.style.border = '1px solid #dee2e6';
      debugButton.style.borderRadius = '4px';
      debugButton.style.cursor = 'pointer';
      
      // Add click handler
      debugButton.addEventListener('click', () => {
        console.group('FIELD MAPPINGS DEBUG INFO');
        console.log('Current mappings:', mappings);
        console.log('Available fields per object:');
        Object.keys(salesforceFields).forEach(objName => {
          console.log(`${objName}: ${salesforceFields[objName].length} fields available`);
        });
        
        // Check for problematic mappings
        const problemMappings = mappings.filter(mapping => {
          if (!mapping.salesforceField) return false;
          
          const objectFields = salesforceFields[mapping.salesforceObject] || [];
          return !objectFields.some(field => field.name === mapping.salesforceField);
        });
        
        if (problemMappings.length > 0) {
          console.warn('Problem mappings detected:', problemMappings);
        } else {
          console.log('All mappings appear valid');
        }
        
        console.groupEnd();
      });
      
      // Add to document
      document.body.appendChild(debugButton);
      
      // Cleanup on unmount
      return () => {
        document.body.removeChild(debugButton);
      };
    }
  }, [mappings, salesforceFields]);
  
  if (loading && opportunityFields.length === 0) {
    return (
      <Container className="mt-4">
        <Card>
          <Card.Body className="text-center p-5">
            <Spinner animation="border" role="status" className="mb-3">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <h5>Loading Field Mappings...</h5>
            <p className="text-muted">
              Retrieving Salesforce field metadata and existing mappings. This may take a few moments.
            </p>
          </Card.Body>
        </Card>
      </Container>
    );
  }
  
  return (
    <div className="page-container">
      <div className="field-mappings-container">
        {/* Sidebar */}
        <div className="field-mappings-sidebar">
          <FieldMappingsSideNav 
            categories={categories}
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            mappingStats={calculateMappingStats()}
          />
        </div>
        
        {/* Main Content */}
        <div className="field-mappings-content">
          
          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Body>
                  
                  
                  {error && (
                    <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>
                  )}
                  
                  {success && (
                    <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>
                  )}
                  
                  <p className="text-muted small mb-0">
                    <i>Tip: You can type in the dropdowns to search for specific fields.</i>
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          
          {/* Primary Field Mapping Card */}
          <Card>
            <Card.Body style={{ padding: '1rem' }}>
              <Row className="mb-2">
                <Col>
                  <h5 style={{ fontSize: '1rem' }}>
                    {categories[activeSection]} Fields
                    <Badge bg="primary" className="ms-2">
                      {getFilteredFields().length} 
                    </Badge>
                  </h5>
                </Col>
              </Row>
              
              <ListGroup style={{ fontSize: '0.9rem' }}>
                {getFilteredFields().map((field, index) => {
                  const mapping = getMappingForField(field.name);
                  const isMapped = !!mapping.salesforceField;
                  const mappedClass = isMapped ? 'mapped-field' : '';
                  
                  return (
                    <ListGroup.Item 
                      key={`${field.name}-${mapping.salesforceObject || selectedObject}-${mapping.salesforceField || ''}`}
                      className={mappedClass}
                    >
                      <Row>
                        <Col md={3}>
                          <strong style={{ fontSize: '0.9rem' }}>{field.label || field.name}</strong>
                          <div className="text-muted small" style={{ fontSize: '0.8rem' }}>{field.name}</div>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label style={{ fontSize: '0.8rem', marginBottom: '0.2rem' }}>Salesforce Object</Form.Label>
                            <Select
                              value={
                                salesforceObjects.find(obj => obj.value === (mapping.salesforceObject || selectedObject))
                              }
                              onChange={(option) => {
                                const newObject = option.value;
                                handleMappingObjectChange(field.name, { value: newObject });
                              }}
                              options={salesforceObjects}
                              styles={customStyles}
                              menuPortalTarget={document.body}
                              isSearchable={true}
                              placeholder="Select or search for an object..."
                              classNamePrefix="select"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label style={{ fontSize: '0.8rem', marginBottom: '0.2rem' }}>Salesforce Field</Form.Label>
                            <Select
                              value={
                                (() => {
                                  const objectName = mapping.salesforceObject || selectedObject;
                                  const availableFields = salesforceFields[objectName] || [];
                                  const selectedField = mapping.salesforceField || '';
                                  const fieldOption = availableFields.find(f => f.name === selectedField);
                                  
                                  return selectedField ? {
                                    value: selectedField,
                                    label: fieldOption ? fieldOption.label || fieldOption.name : selectedField
                                  } : null;
                                })()
                              }
                              onChange={(option) => {
                                const newField = option ? option.value : '';
                                handleFieldChange(field.name, newField);
                              }}
                              options={
                                (() => {
                                  const objectName = mapping.salesforceObject || selectedObject;
                                  const availableFields = salesforceFields[objectName] || [];
                                  
                                  return [
                                    { value: '', label: 'Select Salesforce field' },
                                    ...availableFields.map(sfField => ({
                                      value: sfField.name,
                                      label: sfField.label || sfField.name
                                    }))
                                  ];
                                })()
                              }
                              styles={customStyles}
                              menuPortalTarget={document.body}
                              isSearchable={true}
                              placeholder="Select or search for a field..."
                              classNamePrefix="select"
                              key={`${field.name}-${mapping.salesforceObject || selectedObject}`}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
              
              {getFilteredFields().length === 0 && (
                <Alert variant="info" className="mt-3">
                  No fields found in this category.
                </Alert>
              )}
            </Card.Body>
            <Card.Footer style={{ padding: '0.75rem 1rem' }}>
              <Button 
                variant="primary" 
                onClick={handleSave}
                disabled={saving}
                className="me-2"
                size="sm"
              >
                {saving ? <><Spinner size="sm" animation="border" className="me-1" /> Saving...</> : 'Save Mappings'}
              </Button>
              <Button 
                variant="info" 
                onClick={handleTest}
                disabled={testing}
                size="sm"
              >
                {testing ? <><Spinner size="sm" animation="border" className="me-1" /> Testing...</> : 'Test Mappings'}
              </Button>
            </Card.Footer>
          </Card>
        </div>
      </div>
      
      {/* Test Results Modal */}
      <Modal show={showTestResults} onHide={handleCloseTestResults} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Field Mapping Test Results</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {testResults && (
            <>
              <Alert variant={testResults.success ? "info" : "warning"}>
                {testResults.message}
              </Alert>
              
              <h6>Results:</h6>
              <ListGroup>
                {testResults.results && testResults.results.map((result, index) => (
                  <ListGroup.Item 
                    key={index}
                    variant={result.valid ? "success" : "danger"}
                  >
                    <Row>
                      <Col xs={1}>
                        {result.valid ? (
                          <FaCheck className="text-success" />
                        ) : (
                          <FaTimes className="text-danger" />
                        )}
                      </Col>
                      <Col>
                        <strong>{result.opportunityField}</strong>
                        {' → '}
                        <strong>{result.salesforceObject}.{result.salesforceField}</strong>
                      </Col>
                    </Row>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseTestResults}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default FieldMappings; 